import L from 'leaflet'
import 'leaflet-rotatedmarker'
import { t } from 'i18next'
import { FlightViewMapLibWeather } from './weather'
import logo from './img/mapbox-logo-white.png'
import { formatDistance } from 'date-fns'
import { InitProps, WeatherZoneName } from './types'
import { createElement } from './utils'
import {getLocale} from "./tracker/utils";

export const mapStyles = [
  { name: 'classic', label: 'Classic', id: 'oagmaps/clsbruv0p00v701ql2omgda4a' },
  { name: 'streets', label: 'Streets', id: 'oagmaps/cl7uaarfj009215rym3n7tr87' },
  { name: 'basic', label: 'Basic', id: 'oagmaps/clegvsjcg002p01nv2wxztg75' },
  { name: 'satellite', label: 'Satellite', id: 'mapbox/satellite-v9' },
  { name: 'satellite-streets', label: 'Satellite Streets', id: 'oagmaps/ckyx2u073006414qllfa7tjhm' },
  { name: 'monochrome-light', label: 'Monochrome Light', id: 'oagmaps/ckyx36na0006614ql4qyc4qa3' },
  { name: 'monochrome-dark', label: 'Monochrome Dark', id: 'oagmaps/ckyx35qgs000l15n0452p0fj7' },
  { name: 'outdoors', label: 'Outdoors', id: 'oagmaps/cl7uaze1y001315n0pltsdxba' }
]

export class FlightViewMapLibMap {
  weather: FlightViewMapLibWeather
  mapStyle: string
  timeFormat: string
  showWeather: boolean
  showWeatherLegend: boolean
  weatherZone: WeatherZoneName
  showSettings: boolean
  useMetricScale: boolean

  mapContainer: HTMLElement
  map: L.Map
  imperialScale: L.Control
  metricScale: L.Control
  lastUpdateLayer: L.Control
  scaleLayer: L.Layer
  legendLayer: L.Layer
  tileLayer: L.TileLayer
  tileLayers: { [key: string]: L.Layer } = {}
  mins: number
  scaleLayerPosition: L.ControlPosition
  lastUpdatePosition: L.ControlPosition
  lastUpdated = new Date()
  lastUpdatedInterval: number
  textOverlay: HTMLElement
  locale: Locale

  constructor (parentElement: HTMLElement, props: InitProps, mapContainerId: string = 'fvlib-map') {
    const canMove = props.interactive !== undefined ? props.interactive : true
    this.mapContainer = document.getElementById(mapContainerId)
    if (this.mapContainer === null) {
      this.mapContainer = createElement(mapContainerId)
      if (props.mapHeight) {
        this.mapContainer.style.height = `${props.mapHeight}px`
      }
      parentElement.appendChild(this.mapContainer)
    }
    this.map = L.map(this.mapContainer, { dragging: canMove, zoomControl: canMove, scrollWheelZoom: canMove, zoomSnap: 0.25 })
    this.map.createPane('city-names')
    this.map.getPane('city-names').style.zIndex = '500'
    this.showWeather = props.showWeather !== undefined ? props.showWeather : true
    this.showWeatherLegend = this.showWeather && props.type === 'tracker'
    this.weatherZone = props.weatherZone
    this.showSettings = props.type === 'tracker'
    this.useMetricScale = props.useMetricScale !== undefined ? props.useMetricScale : false
    this.mapStyle = props.mapStyle || 'streets'
    this.timeFormat = props.timeFormat || 'hh:mm a'
    this.initializeMap()
    this.textOverlay = this.mapContainer.appendChild(document.createElement('div'))
    this.textOverlay.id = 'textOverlay'
    this.textOverlay.className = 'text-overlay'
    this.textOverlay.innerText = t('TRACKER.MAP_PART.PLEASE_WAIT')
    this.mins = props.type === 'wl' ? props.wl.mins : 1
    this.scaleLayerPosition = props.scaleLayerPosition || 'bottomright'
    this.lastUpdatePosition = props.lastUpdatePosition || 'bottomright'
    this.locale = getLocale(props.locale || 'en')
    this.lastUpdatedInterval = setInterval(() => { this.lastUpdate() }, 30000)
  }

  initializeMap () {
    this.map.on('layeradd', this.onLayerAdd, this)
    this.map.on('layerremove', this.onLayerRemove, this)
    this.map.attributionControl.setPrefix('<a href="https://leafletjs.com" title="A JS library for interactive maps">Leaflet</a>')
  }

  ready (success: boolean, overlays: L.Control.LayersObject = {}) {
    if (success) {
      this.addAttributionControl()
      this.addControls(overlays)
      this.textOverlay.style.display = 'none'
      if (this.showWeather) {
        if (this.mins <= 1) {
          this.weather = new FlightViewMapLibWeather({
            map: this.map,
            legendLayer: this.legendLayer,
            legend: this.showWeatherLegend,
            zone: this.weatherZone
          })
        }
      }
    } else {
      this.textOverlay.innerText = t('TRACKER.MAP_PART.WE_ARE_SORRY')
    }
  }

  updateMessage (translationKey: string, parameters: object = {}) {
    if (this.textOverlay) {
      this.textOverlay.innerText = t(translationKey, parameters)
    }
  }

  updateLastUpdated () {
    this.lastUpdated = new Date()
    this.lastUpdate()
  }

  cancelUpdate () {
    this.lastUpdateLayer?.remove()
    clearInterval(this.lastUpdatedInterval)
  }

  updateLastUpdatedMessage (container: HTMLElement) {
    const now = new Date()
    const diff = now.getTime() - this.lastUpdated.getTime()
    const minutes = Math.floor(diff / (1000 * 60))
    container.innerText = t('TRACKER.MAP_PART.LAST_UPDATE', { status: formatDistance(this.lastUpdated, now, { addSuffix: true, locale: this.locale }) })

    if (minutes >= 5) {
      container.style.color = 'red'
    } else {
      container.style.color = 'black'
    }
  }

  addAttributionControl () {
    if (process.env.MAPBOX_TOKEN) {
      const mapboxLogoControl = new L.Control({ position: this.showWeatherLegend ? 'bottomleft' : 'topright' })
      mapboxLogoControl.onAdd = function () {
        const img = L.DomUtil.create('img', 'mapbox-logo')
        img.src = logo
        return img
      }
      mapboxLogoControl.addTo(this.map)
    }
  }

  lastUpdate () {
    if (this.lastUpdateLayer) {
      const container = this.lastUpdateLayer.getContainer()
      this.updateLastUpdatedMessage(container)
    } else {
      const LastUpdateControl = L.Control.extend({
        options: {
          position: this.lastUpdatePosition
        },
        onAdd: () => {
          const container = L.DomUtil.create('div', 'last-updated-control leaflet-control')
          this.updateLastUpdatedMessage(container)
          return container
        }
      })
      this.lastUpdateLayer = new LastUpdateControl()
      this.map.addControl(this.lastUpdateLayer)
    }
  }

  addMapTileLayer (mapStyle: string) {
    if (this.tileLayer) {
      this.tileLayer.remove()
    }
    if (process.env.MAPBOX_TOKEN) {
      this.tileLayer = L.tileLayer('https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}', {
        attribution: '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://www.oag.com/">OAG</a>',
        maxZoom: 18,
        id: mapStyles.find((style) => style.label === mapStyle)?.id || mapStyles.find((style) => style.name === mapStyle)?.id || mapStyle,
        tileSize: 512,
        zoomOffset: -1,
        accessToken: process.env.MAPBOX_TOKEN
      }).addTo(this.map)
    } else {
      this.tileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: `&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> ${t('TRACKER.MAP_PART.CONTRIBUTORS')} | ${t('TRACKER.MAP_PART.FLIGHT_DATA_BY')} <a href="https://www.oag.com">OAG</a>`
      }).addTo(this.map)
    }
  }

  addControls (overlays: L.Control.LayersObject = {}) {
    this.imperialScale = L.control.scale({ metric: false, position: this.scaleLayerPosition })
    this.metricScale = L.control.scale({ imperial: false, position: this.scaleLayerPosition })
    this.scaleLayer = L.layerGroup()
    this.legendLayer = L.layerGroup()
    this.legendLayer.addTo(this.map)
    this.tileLayers.Default = L.layerGroup()
    for (const style of mapStyles.filter((style) => style.name !== this.mapStyle)) {
      this.tileLayers[style.label] = L.layerGroup()
    }
    this.tileLayers.Default?.addTo(this.map)
    this.lastUpdate()

    if (this.showSettings) {
      L.control.layers(this.tileLayers, {
        ...overlays,
        'Metric scale': this.scaleLayer,
        Legends: this.legendLayer
      }).addTo(this.map)
    }
    if (this.useMetricScale) {
      this.metricScale.addTo(this.map)
    } else {
      this.imperialScale.addTo(this.map)
    }
  }

  onLayerAdd (event: L.LayerEvent) {
    if (event.layer === this.scaleLayer) {
      this.useMetricScale = true
      this.map.removeControl(this.imperialScale)
      this.map.addControl(this.metricScale)
    } else if (event.layer === this.legendLayer) {
      this.map.addControl(this.useMetricScale ? this.metricScale : this.imperialScale)
      if (this.lastUpdateLayer) {
        this.map.addControl(this.lastUpdateLayer)
      }
    } else {
      for (const [key, value] of Object.entries(this.tileLayers)) {
        if (event.layer === value) {
          this.addMapTileLayer(key === 'Default' ? this.mapStyle : key)
          break
        }
      }
    }
  }

  onLayerRemove (event: L.LayerEvent) {
    if (event.layer === this.scaleLayer) {
      this.useMetricScale = false
      this.map.removeControl(this.metricScale)
      this.map.addControl(this.imperialScale)
    } else if (event.layer === this.legendLayer) {
      this.map.removeControl(this.useMetricScale ? this.metricScale : this.imperialScale)
      if (this.lastUpdateLayer) {
        this.map.removeControl(this.lastUpdateLayer)
      }
    } else {
      this.map.removeLayer(event.layer)
    }
  }
}
